import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";

export interface FiltrosAvancados {
  servidor: string;
  sexo: string;
  zonaRural: string;
  idadeMin: string;
  idadeMax: string;
  profissao: string;
  bairro: string;
  cadastroInicio: string;
  cadastroFim: string;
}

export const AVANCADOS_VAZIOS: FiltrosAvancados = {
  servidor: "todos",
  sexo: "todos",
  zonaRural: "todas",
  idadeMin: "",
  idadeMax: "",
  profissao: "",
  bairro: "",
  cadastroInicio: "",
  cadastroFim: "",
};

interface Props {
  open: boolean;
  valores: FiltrosAvancados;
  onAplicar: (v: FiltrosAvancados) => void;
  onFechar: () => void;
}

/** Card de seção (título + corpo) — dá o visual agrupado ao modal */
function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "action.hover",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>{titulo}</Typography>
      </Box>
      <Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 1.5 }}>{children}</Box>
    </Box>
  );
}

export const FiltrosAvancadosModal: React.FC<Props> = ({
  open,
  valores,
  onAplicar,
  onFechar,
}) => {
  const [f, setF] = useState<FiltrosAvancados>(valores);

  // recopia os valores aplicados toda vez que abre (edição em rascunho)
  useEffect(() => {
    if (open) setF(valores);
  }, [open]);

  const set =
    (campo: keyof FiltrosAvancados) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setF((prev) => ({ ...prev, [campo]: e.target.value }));

  return (
    <Dialog
      open={open}
      onClose={onFechar}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Cabeçalho */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2,
          background: "linear-gradient(135deg, #2563eb 0%, #4f8ef7 100%)",
          color: "#fff",
        }}
      >
        <FilterListIcon />
        <Typography sx={{ fontWeight: 800, fontSize: 16, flex: 1 }}>
          Filtros avançados
        </Typography>
        <IconButton onClick={onFechar} size="small" sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          bgcolor: "background.default",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Secao titulo="Perfil">
          <TextField
            select
            label="Tipo de servidor"
            value={f.servidor}
            onChange={set("servidor")}
            size="small"
            sx={{ flex: 1, minWidth: 160 }}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="servidor">Servidor</MenuItem>
            <MenuItem value="nao-servidor">Não servidor</MenuItem>
          </TextField>
          <TextField
            select
            label="Gênero"
            value={f.sexo}
            onChange={set("sexo")}
            size="small"
            sx={{ flex: 1, minWidth: 160 }}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="M">Masculino</MenuItem>
            <MenuItem value="F">Feminino</MenuItem>
          </TextField>
          <TextField
            select
            label="Localidade"
            value={f.zonaRural}
            onChange={set("zonaRural")}
            size="small"
            sx={{ flex: 1, minWidth: 160 }}
          >
            <MenuItem value="todas">Todas</MenuItem>
            <MenuItem value="rural">Zona rural</MenuItem>
            <MenuItem value="urbana">Zona urbana</MenuItem>
          </TextField>
        </Secao>

        <Secao titulo="Dados">
          <TextField
            label="Profissão"
            value={f.profissao}
            onChange={set("profissao")}
            size="small"
            sx={{ flex: 1, minWidth: 180 }}
          />
          <TextField
            label="Bairro"
            value={f.bairro}
            onChange={set("bairro")}
            size="small"
            sx={{ flex: 1, minWidth: 180 }}
          />
        </Secao>

        <Secao titulo="Idade">
          <TextField
            label="Idade mínima"
            type="number"
            value={f.idadeMin}
            onChange={set("idadeMin")}
            size="small"
            sx={{ flex: 1, minWidth: 140 }}
          />
          <TextField
            label="Idade máxima"
            type="number"
            value={f.idadeMax}
            onChange={set("idadeMax")}
            size="small"
            sx={{ flex: 1, minWidth: 140 }}
          />
        </Secao>

        <Secao titulo="Período de cadastro">
          <TextField
            label="De"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={f.cadastroInicio}
            onChange={set("cadastroInicio")}
            size="small"
            sx={{ flex: 1, minWidth: 160 }}
          />
          <TextField
            label="Até"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={f.cadastroFim}
            onChange={set("cadastroFim")}
            size="small"
            sx={{ flex: 1, minWidth: 160 }}
          />
        </Secao>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button onClick={() => setF(AVANCADOS_VAZIOS)} sx={{ fontWeight: 600, color: "error.main" }}>
          Limpar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onFechar} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancelar
        </Button>
        <Button
          onClick={() => {
            onAplicar(f);
            onFechar();
          }}
          variant="contained"
          disableElevation
          sx={{ fontWeight: 700, px: 4, borderRadius: 2 }}
        >
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
